import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { EmptyState } from '../components/EmptyState';
import {
  ATLAS_UNMAPPED,
  PROJECTS_OVERVIEW,
  SET_IGNORED,
  type AtlasProjectOverview,
  type UnmappedCluster,
} from '../lib/atlas';

/**
 * `/atlas-whitelist/mappings` — cluster → Atlas project, and the unmapped worklist.
 *
 * The worklist is the part that matters. Every agent in an unmapped cluster gets
 * `no_targets` on register: its node IP reaches no allowlist and its pods stay
 * `0/1` READY, because readiness is gated on Atlas convergence. The old page
 * rendered that — 27 clusters' worth — as names joined with `', '` inside a single
 * `colSpan={3}` cell, with no way to act on any one of them.
 */

const MAPPINGS = gql`
  query ClusterAtlasMappings {
    clusterAtlasMappings {
      clusterName
      atlasProjectKey
      priority
      projectDisplayName
      projectIsActive
    }
    clusters {
      name
    }
  }
`;

const SET_MAPPING = gql`
  mutation SetClusterAtlasProject($clusterName: String!, $atlasProjectKey: String!) {
    setClusterAtlasProject(clusterName: $clusterName, atlasProjectKey: $atlasProjectKey) {
      clusterName
      atlasProjectKey
    }
  }
`;

const REMOVE_MAPPING = gql`
  mutation RemoveClusterAtlasProject($clusterName: String!, $atlasProjectKey: String!) {
    removeClusterAtlasProject(clusterName: $clusterName, atlasProjectKey: $atlasProjectKey)
  }
`;

const REPORTED_BY_CLUSTER = gql`
  query AtlasReportedByCluster {
    atlasReportedIps {
      id
      clusterName
    }
  }
`;

interface Mapping {
  clusterName: string;
  atlasProjectKey: string;
  priority: number;
  projectDisplayName?: string | null;
  projectIsActive: boolean;
}

export function AtlasMappingsPage() {
  const { data, loading, error, refetch } = useQuery<{
    clusterAtlasMappings: Mapping[];
    clusters: { name: string }[];
  }>(MAPPINGS, { fetchPolicy: 'cache-and-network' });
  const overview = useQuery<{ atlasProjectsOverview: AtlasProjectOverview[] }>(PROJECTS_OVERVIEW, {
    fetchPolicy: 'cache-and-network',
  });
  const unmappedQ = useQuery<{ atlasUnmappedClusters: UnmappedCluster[] }>(ATLAS_UNMAPPED, {
    fetchPolicy: 'cache-and-network',
  });
  const reported = useQuery<{ atlasReportedIps: { id: string; clusterName?: string | null }[] }>(
    REPORTED_BY_CLUSTER,
    { fetchPolicy: 'cache-and-network' },
  );

  const [cluster, setCluster] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const done = () => {
    setActionError(null);
    void refetch();
    void unmappedQ.refetch();
  };
  const fail = (e: unknown) => setActionError((e as Error).message);
  const [setMapping, setState] = useMutation(SET_MAPPING, { onCompleted: done, onError: fail });
  const [removeMapping, removeState] = useMutation(REMOVE_MAPPING, {
    onCompleted: done,
    onError: fail,
  });
  const [setIgnored, ignoreState] = useMutation(SET_IGNORED, { onCompleted: done, onError: fail });
  const busy = setState.loading || removeState.loading || ignoreState.loading;

  const mappings = data?.clusterAtlasMappings ?? [];
  const clusters = data?.clusters ?? [];
  const projects = (overview.data?.atlasProjectsOverview ?? []).filter((p) => p.isActive);
  const unmapped = unmappedQ.data?.atlasUnmappedClusters ?? [];
  const active = unmapped.filter((c) => !c.ignored);
  const ignored = unmapped.filter((c) => c.ignored);

  // How many node IPs each mapping actually carries. New column: a mapping with 84
  // IPs behind it and one with none are very different things to remove.
  const ipsByCluster = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of reported.data?.atlasReportedIps ?? []) {
      if (!r.clusterName) continue;
      m.set(r.clusterName, (m.get(r.clusterName) ?? 0) + 1);
    }
    return m;
  }, [reported.data]);

  const byCluster = new Map<string, Mapping[]>();
  for (const m of mappings) {
    byCluster.set(m.clusterName, [...(byCluster.get(m.clusterName) ?? []), m]);
  }

  return (
    <div className="page">
      <Link className="atlas-back" to="/atlas-whitelist">
        ← Atlas IP whitelist
      </Link>

      <div className="detail-head">
        <h2>Cluster → Atlas project</h2>
        <span className="badge quiet">
          {mappings.length} mapping{mappings.length === 1 ? '' : 's'}
        </span>
      </div>

      <p className="detail-desc">
        An agent whitelists its node&apos;s egress IP into <strong>every</strong> project mapped to
        its cluster. With none, register returns <code>no_targets</code>, the IP reaches no
        allowlist, and the pods stay <code>0/1</code> READY because readiness is gated on Atlas
        convergence.
      </p>

      {(error || actionError) && <p className="error">{error?.message ?? actionError}</p>}

      <div className="filter-row">
        <select
          className="class-select"
          value={cluster}
          onChange={(e) => setCluster(e.target.value)}
          aria-label="Cluster"
        >
          <option value="">Select cluster…</option>
          {clusters.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name}
              {byCluster.has(c.name) ? '' : ' — unmapped'}
            </option>
          ))}
        </select>
        <span className="muted">→</span>
        <select
          className="class-select"
          value={projectKey}
          onChange={(e) => setProjectKey(e.target.value)}
          aria-label="Atlas project"
        >
          <option value="">Select project…</option>
          {projects.map((p) => (
            <option key={p.projectKey} value={p.projectKey}>
              {p.displayName || p.projectKey}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!cluster || !projectKey || busy}
          onClick={() => {
            void setMapping({ variables: { clusterName: cluster, atlasProjectKey: projectKey } });
            setProjectKey('');
          }}
        >
          {setState.loading ? 'Adding…' : 'Add mapping'}
        </button>
      </div>

      <p className="text-xs muted" style={{ marginTop: -4 }}>
        Both sides are dropdowns on purpose. <code>atlas_project_key</code> has a foreign key so a
        bad key fails loudly, but <code>cluster_name</code> is a bare string — a typo there inserts
        a mapping that matches no agent, and the only symptom is a cluster stuck on{' '}
        <code>no_targets</code>.
      </p>

      {loading && !mappings.length ? (
        <p className="muted">Loading mappings…</p>
      ) : (
        <div className="atlas-card">
          <table className="atlas-table">
            <thead>
              <tr>
                <th>Cluster</th>
                <th>Atlas projects</th>
                <th>Node IPs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...byCluster.entries()].map(([name, list]) => (
                <tr key={name}>
                  <td className="mono">{name}</td>
                  <td>
                    {list.map((m) => (
                      <span key={m.atlasProjectKey} className="env-chip" style={{ marginRight: 6 }}>
                        {m.projectDisplayName || m.atlasProjectKey}
                        {/* A mapping whose project row is gone or deactivated still
                            counts as a target and fails at push time. */}
                        {!m.projectIsActive && <span className="muted"> (inactive)</span>}
                      </span>
                    ))}
                  </td>
                  <td className="mono">{ipsByCluster.get(name) ?? 0}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {list.map((m) => (
                      <button
                        key={m.atlasProjectKey}
                        type="button"
                        className="mini-btn"
                        disabled={busy}
                        onClick={() => {
                          if (!confirm(`Remove ${name} → ${m.atlasProjectKey}?`)) return;
                          void removeMapping({
                            variables: { clusterName: name, atlasProjectKey: m.atlasProjectKey },
                          });
                        }}
                      >
                        Remove {m.atlasProjectKey}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
              {!byCluster.size && (
                <tr>
                  <td colSpan={4} className="muted">
                    No cluster is mapped to an Atlas project yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <UnmappedWorklist
        rows={active}
        ignored={ignored}
        projects={projects}
        busy={busy}
        onMap={(clusterName, key) =>
          void setMapping({ variables: { clusterName, atlasProjectKey: key } })
        }
        onIgnore={(clusterName, ignore) =>
          void setIgnored({ variables: { clusterName, ignored: ignore, reason: null } })
        }
      />
    </div>
  );
}

/**
 * The worklist. A list of broken clusters with an action on every row, not a
 * sentence.
 *
 * `Ignore` exists because without it the list can never reach zero — some clusters
 * genuinely have no MongoDB behind them — and a list that always shows a red count
 * is one people stop reading. It records the decision rather than hiding it: the
 * ignored set stays on the page, collapsed.
 */
function UnmappedWorklist({
  rows,
  ignored,
  projects,
  busy,
  onMap,
  onIgnore,
}: {
  rows: UnmappedCluster[];
  ignored: UnmappedCluster[];
  projects: AtlasProjectOverview[];
  busy: boolean;
  onMap: (clusterName: string, projectKey: string) => void;
  onIgnore: (clusterName: string, ignore: boolean) => void;
}) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkKey, setBulkKey] = useState('');
  const [showIgnored, setShowIgnored] = useState(false);

  const needle = q.trim().toLowerCase();
  const filtered = rows.filter((r) => !needle || r.clusterName.toLowerCase().includes(needle));
  const waiting = rows.reduce((n, r) => n + r.agentCount, 0);

  const toggle = (name: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  if (!rows.length && !ignored.length) {
    return (
      <div style={{ marginTop: '1.5rem' }}>
        <EmptyState
          icon="check"
          title="Every cluster has an Atlas project"
          body="No agent is reporting no_targets. New clusters appear here as soon as they are scanned."
        />
      </div>
    );
  }

  return (
    <section className="atlas-worklist">
      <div className="atlas-worklist__head">
        <span className="atlas-dot atlas-dot--lg" style={{ background: 'var(--amber)' }} />
        <h3>
          {rows.length} cluster{rows.length === 1 ? '' : 's'} ha
          {rows.length === 1 ? 's' : 've'} no Atlas project
        </h3>
        <span className="filter-count">
          {waiting} agent{waiting === 1 ? '' : 's'} waiting
        </span>
      </div>

      <p className="text-xs muted">
        Every agent in these clusters reports <code>no_targets</code> — its node IP reaches no
        allowlist and its pods stay <code>0/1</code> READY. Map each one, or mark it as
        intentionally unmapped so it stops appearing here.
      </p>

      {rows.length > 0 && (
        <>
          <div className="filter-row">
            <input
              type="search"
              placeholder="Filter clusters…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button
              type="button"
              className="mini-btn"
              onClick={() =>
                setPicked((prev) =>
                  prev.size === filtered.length
                    ? new Set()
                    : new Set(filtered.map((r) => r.clusterName)),
                )
              }
            >
              {picked.size === filtered.length && filtered.length ? 'Clear selection' : 'Select all'}
            </button>
            {/* Bulk assign, because with twenty-plus rows one-at-a-time is the wrong
                default — these clusters usually arrive in batches from one k8s
                upgrade or one new environment. */}
            <select
              className="class-select"
              value={bulkKey}
              onChange={(e) => setBulkKey(e.target.value)}
              aria-label="Map selected to"
              disabled={!picked.size}
            >
              <option value="">Map selected to…</option>
              {projects.map((p) => (
                <option key={p.projectKey} value={p.projectKey}>
                  {p.displayName || p.projectKey}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              disabled={!picked.size || !bulkKey || busy}
              onClick={() => {
                for (const name of picked) onMap(name, bulkKey);
                setPicked(new Set());
                setBulkKey('');
              }}
            >
              Map {picked.size || ''}
            </button>
          </div>

          <div className="atlas-worklist__rows">
            {filtered.map((r) => (
              <div className="atlas-work-row" key={r.clusterName}>
                <input
                  type="checkbox"
                  checked={picked.has(r.clusterName)}
                  onChange={() => toggle(r.clusterName)}
                  aria-label={`Select ${r.clusterName}`}
                />
                <span className="mono atlas-work-row__name">{r.clusterName}</span>
                <span
                  className="atlas-work-row__agents"
                  style={{ color: r.agentCount ? 'var(--amber)' : 'var(--faint)' }}
                >
                  {r.agentCount
                    ? `${r.agentCount} agent${r.agentCount === 1 ? '' : 's'} waiting`
                    : 'no agent'}
                </span>
                <select
                  className="class-select"
                  value=""
                  disabled={busy}
                  aria-label={`Map ${r.clusterName} to a project`}
                  onChange={(e) => e.target.value && onMap(r.clusterName, e.target.value)}
                >
                  <option value="">Map to project…</option>
                  {projects.map((p) => (
                    <option key={p.projectKey} value={p.projectKey}>
                      {p.displayName || p.projectKey}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="mini-btn"
                  disabled={busy}
                  onClick={() => onIgnore(r.clusterName, true)}
                  title="This cluster needs no Atlas project"
                >
                  Ignore
                </button>
              </div>
            ))}
            {!filtered.length && <p className="muted text-xs">No cluster matches that filter.</p>}
          </div>
        </>
      )}

      {ignored.length > 0 && (
        <div className="atlas-ignored">
          <button type="button" className="mini-btn" onClick={() => setShowIgnored((v) => !v)}>
            {showIgnored ? 'Hide' : 'Show'} {ignored.length} intentionally unmapped
          </button>
          {showIgnored && (
            <div className="atlas-worklist__rows">
              {ignored.map((r) => (
                <div className="atlas-work-row" key={r.clusterName}>
                  <span className="mono atlas-work-row__name">{r.clusterName}</span>
                  <span className="atlas-work-row__agents muted">
                    {r.ignoredReason || 'no reason recorded'}
                    {r.ignoredBy ? ` · ${r.ignoredBy}` : ''}
                  </span>
                  <button
                    type="button"
                    className="mini-btn"
                    disabled={busy}
                    onClick={() => onIgnore(r.clusterName, false)}
                  >
                    Un-ignore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
